import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { Button, Card, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { authApi } from '../../lib/auth-api';

const Schema = z
  .object({
    email: z.string().trim().toLowerCase().email(),
    token: z.string().min(1, 'Reset token is missing'),
    password: z.string().min(8, 'At least 8 characters'),
    passwordConfirmation: z.string().min(8),
  })
  .refine((d) => d.password === d.passwordConfirmation, {
    message: 'Passwords do not match',
    path: ['passwordConfirmation'],
  });
type FormValues = z.infer<typeof Schema>;

function ResetPassword() {
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { token?: string; email?: string };
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: { token: search.token ?? '', email: search.email ?? '' },
  });

  const onSubmit = handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      await authApi.resetPassword(values);
      notifications.show({ message: 'Password updated. Please sign in.', color: 'green' });
      navigate({ to: '/auth/login' });
    } catch (err) {
      notifications.show({
        message: (err as Error).message || 'Reset failed',
        color: 'red',
      });
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
      <Card>
        <Stack gap="sm">
          <Text fw={600} size="lg">
            Reset password
          </Text>
          <form onSubmit={onSubmit}>
            <Stack gap="sm">
              <TextInput label="Email" {...register('email')} error={errors.email?.message} />
              <input type="hidden" {...register('token')} />
              <PasswordInput
                label="New password"
                autoComplete="new-password"
                {...register('password')}
                error={errors.password?.message}
              />
              <PasswordInput
                label="Confirm password"
                autoComplete="new-password"
                {...register('passwordConfirmation')}
                error={errors.passwordConfirmation?.message}
              />
              <Button type="submit" loading={submitting}>
                Reset password
              </Button>
            </Stack>
          </form>
        </Stack>
      </Card>
    </motion.div>
  );
}

export const Route = createFileRoute('/auth/reset-password')({ component: ResetPassword });
